import type { Observable} from 'rxjs';
import { of, forkJoin } from 'rxjs';
import { defaultIfEmpty, map, mergeMap } from 'rxjs/operators';
import type {
    CrossHierarchyLookup,
    Datum,
    QueryChartInteractionDatapoint,
    QueryChartInteractionDatapointMultiSelect,
} from '@bayer-int/portletce-aclibv2';
import type {
    DimensionNode,
    HierarchyNode} from '@bayer-int/portletce-shared';
import {
    isNotNullable,
    head,
    zip,
    toArray,
} from '@bayer-int/portletce-shared';
import { isEmpty, uniqBy, groupBy, difference } from 'lodash-es';

import type {
    FilterDataArray,
    FilterId,
    FilterData,
    HierarchyFilterNode,
    ResolvedListenToDefinitions,
    Keyword,
    ResolvedListenToDefinition} from 'portlet/models';
import {
    PortletQuery,
    FilterValue,
    MatchedListenToQueryContext,
} from 'portlet/models';
import type { Hierarchy, Dimension, MasterDataService } from 'portlet/services';
import { mergeFilterNodes } from 'portlet/common/utils/mergeFilterNodes';

import { compareKeywords } from './keywords';
import { InteractionFilterTypeHintProcessor } from './InteractionFilterTypeHintProcessor';
import { QueryChartInteractionDatapointUtil } from './QueryChartInteractionDatapoint';

export class InteractionToFilterService {
    private readonly filterTypeHintProcessor = new InteractionFilterTypeHintProcessor(
        this.masterdata,
    );

    constructor(private readonly masterdata: MasterDataService) {}

    public datumToFilter(
        listenTo: ResolvedListenToDefinitions,
        filterDataArray: FilterDataArray,
        queryMetadata: PortletQuery | null,
        settings: {
            currentHierarchySetting: Hierarchy | null;
            currentDimensionSetting: Dimension | null;
            currentLinkedHierarchiesSetting: Record<Hierarchy['hierarchyId'], Hierarchy>;
            currentApplyFilterOnExactDrillOnlySetting: boolean | null;
            currentApplyFilterToUpstreamHierarchyLevelsSetting: boolean | null;
        },
        datum: Datum,
        eventKeywords: Keyword[],
    ): Observable<FilterDataArray> {
        const {
            currentDimensionSetting,
            currentHierarchySetting,
            currentLinkedHierarchiesSetting,
            currentApplyFilterOnExactDrillOnlySetting,
            currentApplyFilterToUpstreamHierarchyLevelsSetting,
        } = settings;
        const applyFilterToUpstreamHierarchyLevels =
            currentApplyFilterToUpstreamHierarchyLevelsSetting ?? true;
        const filterItems = listenTo.map(({ item }) => item);
        const defaultFilterItemToEmit = InteractionToFilterService.matchFirstListenToByKeywords(
            listenTo,
            eventKeywords,
        );
        const defaultFilterItem = defaultFilterItemToEmit
            ? defaultFilterItemToEmit.item
            : undefined;

        const withDefaultFilterKey = (value: FilterValue | null): Observable<FilterDataArray> =>
            defaultFilterItem && value ? of([{ item: defaultFilterItem, value }]) : of([]);

        const emitAsSimpleSelect = (
            simpleSelectDatum: Datum,
            datapoint: QueryChartInteractionDatapoint | null,
        ) => {
            if (simpleSelectDatum.type !== 'SIMPLE_SELECT') {
                return of([]);
            }
            const matchingFilterItems = InteractionToFilterService.matchListenToByKeywords(
                listenTo,
                eventKeywords,
            );

            const filters$: Array<Observable<FilterData[]>> = matchingFilterItems.map(
                filterItem => {
                    const { item } = filterItem;
                    // column mapping implementation
                    if (!isEmpty(filterItem.columnMapping) && datapoint) {
                        const columnKeysToMap = Object.keys(filterItem.columnMapping);
                        const columnMappedValue = FilterValue.DIMENSION({
                            exclude: false,
                            value: QueryChartInteractionDatapointUtil.toMultiSelect(
                                datapoint,
                            ).items.map(item => {
                                if (columnKeysToMap.includes(item.columnKey) && item.datapoint) {
                                    const columnMapping =
                                        filterItem.columnMapping &&
                                        filterItem.columnMapping[item.columnKey];
                                    const mappedKey =
                                        isNotNullable(columnMapping?.key) &&
                                        item.datapoint[columnMapping.key];
                                    const mappedValue =
                                        isNotNullable(columnMapping?.value) &&
                                        item.datapoint[columnMapping.value];

                                    return {
                                        key: isNotNullable(mappedKey)
                                            ? String(mappedKey)
                                            : item.key,
                                        value: isNotNullable(mappedValue)
                                            ? String(mappedValue)
                                            : item.key,
                                        identifier: '',
                                        internalKey:
                                            filterItem.filterHint?.dimension?.internalKey || '',
                                        characteristicId: '',
                                        basedOnInternalKey:
                                            filterItem.filterHint?.dimension?.internalKey || '',
                                        basedOnCharacteristicId: '',
                                    };
                                }

                                return {
                                    key: item.key,
                                    value: item.value,
                                    identifier: '',
                                    internalKey:
                                        filterItem.filterHint?.dimension?.internalKey || '',
                                    characteristicId: '',
                                    basedOnInternalKey:
                                        filterItem.filterHint?.dimension?.internalKey || '',
                                    basedOnCharacteristicId: '',
                                };
                            }),
                        });
                        return of(columnMappedValue ? [{ item, value: columnMappedValue }] : []);
                    }

                    if (filterItem.filterHint)
                        return this.filterTypeHintProcessor.processFilterTypeHint(
                            filterItem,
                            listenTo,
                            filterDataArray,
                            toArray(simpleSelectDatum.name),
                            eventKeywords,
                            { applyFilterToUpstreamHierarchyLevels },
                        );

                    const value = FilterValue.tryParseDimension(simpleSelectDatum.name);

                    return of(value ? [{ item, value }] : []);
                },
            );

            return forkJoin(filters$);
        };

        /**
         * `datum.type` loosely relates to the source of the interatcion (kind of selector),
         * and very roughly matches available filter types (FilterValue).
         * The exceptions from that are 'SIMPLE_SELECT' and 'QUERY_SELECT', which should be considered as
         * coming from a generic source. Therefore, when handling these events we make use of the filter type hints
         * defined on `ResolvedListenToDefinitions` (see InteractionFilterTypeHintProcessor.processFilterTypeHint).
         * For other events, the logic may be more specific/coupled with selector portlets.
         */
        switch (datum.type) {
            case 'SMART_VIEW': {
                return withDefaultFilterKey(
                    FilterValue.SMART_VIEW_TAG({ viewTag: datum.name.key }),
                );
            }

            case 'SELECT': {
                if (!currentDimensionSetting) {
                    const value = FilterValue.DIMENSION({
                        value: datum.name,
                        exclude: datum.exclude || false,
                    });
                    return withDefaultFilterKey(value);
                }
                if (
                    currentDimensionSetting &&
                    !currentHierarchySetting &&
                    isEmpty(currentLinkedHierarchiesSetting)
                ) {
                    // caution: we make an assumption that `listenTo` may be `settings.INTERNAL_KEY` here,
                    // if that is not the case we pick the default
                    const item = filterItems.includes(currentDimensionSetting.internalKey)
                        ? currentDimensionSetting.internalKey
                        : defaultFilterItem;

                    const filters = item
                        ? [
                              {
                                  item,
                                  value: FilterValue.DIMENSION({
                                      value: datum.name,
                                      exclude: datum.exclude || false,
                                  }),
                              },
                          ]
                        : [];

                    return of(filters);
                }

                if (!currentHierarchySetting) return of([]);

                // first apply the selection to the default hierarchy selector is configured to be in
                // so that we also select/unselect appropiate parent/children nodes
                const { item, nodes: currentFilterNodes } =
                    InteractionToFilterService.findFilterItemAndNodesOfHierarchy(
                        currentHierarchySetting,
                        filterDataArray,
                        filterItems,
                        defaultFilterItem,
                    );

                const mainFilter = item
                    ? {
                          item,
                          value: FilterValue.HIERARCHY({
                              value: {
                                  hierarchyInternalKeys: currentHierarchySetting.internalKeys,
                                  values: mergeFilterNodes(
                                      currentFilterNodes,
                                      datum.name,
                                      currentHierarchySetting,
                                      currentDimensionSetting.internalKey,
                                      { applyFilterToUpstreamHierarchyLevels },
                                  ),
                              },
                              exclude: datum.exclude || false,
                          }),
                      }
                    : null;

                if (!mainFilter) return of([]);

                const mainFilterValueNodes =
                    mainFilter && FilterValue.is.HIERARCHY(mainFilter.value)
                        ? mainFilter.value.value.values
                        : null;

                // note: we use `basedOnInternalKeys` instead of `internalKeys` because this allows us to support
                // cross hierarchy filtering on different dimensions that have been augmented, (and therefore they have different `internalKeys`),
                // but share the 'base' internalKey
                const affectedDimensions = currentHierarchySetting.basedOnInternalKeys;

                // apply each hierarchy level selection to all context hierarchies that the portlet is configured to be in
                const filterDataForContextHierarchies = mainFilterValueNodes
                    ? Object.values(currentLinkedHierarchiesSetting)
                          .map(hierarchy => {
                              const nodes = InteractionToFilterService.translateNodesToHierarchy(
                                  mainFilterValueNodes,
                                  hierarchy,
                                  datum.crossHierarchyLookup,
                              );

                              return InteractionToFilterService.mergeDimensionNodesIntoHierarchyFilter(
                                  nodes,
                                  datum.exclude || false,
                                  hierarchy,
                                  filterDataArray,
                                  filterItems,
                                  defaultFilterItem,
                                  affectedDimensions,
                                  currentApplyFilterToUpstreamHierarchyLevelsSetting ?? true,
                              );
                          })
                          .filter(isNotNullable)
                    : [];

                const filters = uniqBy(
                    [mainFilter, ...filterDataForContextHierarchies],
                    ({ item }) => item,
                );

                return of(filters);
            }

            case 'TREE_SELECT': {
                if (!currentHierarchySetting) {
                    // case of a real hierarchy selector with bwquery input
                    const mainFilterValueNodes: HierarchyFilterNode[] = datum.name.map(
                        ({ node }) => ({
                            identifier: node.identifier,
                            key: node.key,
                            value: node.value,
                            identifierPathToRoot: node.identifierPathToRoot,
                            characteristicId: node.characteristicId,
                            internalKey: node.internalKey,
                            hierarchyId: node.hierarchyId,
                            basedOnInternalKey: node.basedOnInternalKey,
                            basedOnCharacteristicId: node.basedOnCharacteristicId,
                            keyPathToRoot: node.keyPathToRoot,
                            valuePathToRoot: node.valuePathToRoot,
                            nodeName: node.nodeName,
                        }),
                    );

                    const mainFilterValue = FilterValue.HIERARCHY({
                        value: {
                            hierarchyInternalKeys: [],
                            values: mainFilterValueNodes,
                        },
                        exclude: datum.exclude || false,
                    });
                    return withDefaultFilterKey(mainFilterValue);
                }

                if (!currentHierarchySetting || !defaultFilterItem) return of([]);

                const item = filterItems.includes(currentHierarchySetting.hierarchyId)
                    ? currentHierarchySetting.hierarchyId
                    : defaultFilterItem;

                const mainFilterValueNodes = datum.name.map(({ node }) => node);
                const mainFilterValue = FilterValue.HIERARCHY({
                    value: {
                        hierarchyInternalKeys: currentHierarchySetting.internalKeys,
                        values: mainFilterValueNodes,
                    },
                    exclude: datum.exclude || false,
                });
                const mainFilter = { item, value: mainFilterValue };
                // note: we use `basedOnInternalKeys` instead of `internalKeys` because this allows us to support
                // cross hierarchy filtering on different dimensions that have been augmented, (and therefore they have different `internalKeys`),
                // but share the 'base' internalKey
                const affectedDimensions = currentHierarchySetting.basedOnInternalKeys;

                // apply each hierarchy level selection to all context hierarchies that the portlet is configured to be in
                const filterDataForContextHierarchies = Object.values(
                    currentLinkedHierarchiesSetting,
                )
                    .map(hierarchy => {
                        const nodes = InteractionToFilterService.translateNodesToHierarchy(
                            mainFilterValueNodes,
                            hierarchy,
                            datum.crossHierarchyLookup,
                        );

                        return InteractionToFilterService.mergeDimensionNodesIntoHierarchyFilter(
                            nodes,
                            datum.exclude || false,
                            hierarchy,
                            filterDataArray,
                            filterItems,
                            defaultFilterItem,
                            affectedDimensions,
                            currentApplyFilterToUpstreamHierarchyLevelsSetting ?? true,
                        );
                    })
                    .filter(isNotNullable);

                const filters = uniqBy(
                    [mainFilter, ...filterDataForContextHierarchies],
                    ({ item }) => item,
                );

                return of(filters);
            }
            case 'DATE': {
                return withDefaultFilterKey(
                    FilterValue.DATE({ value: datum.name, dateType: datum.dateType }),
                );
            }
            case 'DATE_RANGE': {
                return withDefaultFilterKey(
                    FilterValue.DATE_RANGE({ value: datum.name, dateType: datum.dateType }),
                );
            }
            case 'SIMPLE_SELECT': {
                const filters = emitAsSimpleSelect(datum, null);

                return filters.pipe(
                    map(filter => filter.flat()),
                    defaultIfEmpty<FilterDataArray, FilterDataArray>([]),
                );
            }
            case 'SIMPLE_SELECT_NUMBER': {
                return withDefaultFilterKey(FilterValue.NUMBER({ value: datum.name }));
            }
            case 'SCALE': {
                return withDefaultFilterKey(
                    FilterValue.SCALE({
                        value: { key: datum.name.key, label: datum.name.label },
                    }),
                );
            }
            case 'ROLE': {
                return withDefaultFilterKey(
                    FilterValue.ROLE({
                        value: { key: datum.name.key, label: datum.name.label },
                    }),
                );
            }

            case 'QUERY_SELECT':
            case 'QUERY_SIMPLE_SELECT': {
                // In this type of event we may receive multiple datapoints (`datum.name`) which are not guaranteed to belong to the same
                // characteristic (column in the datatable) and the exact datapoint that was clicked (`datum.cell`).
                //
                // Depending on the value of ApplyFilterOnExactDrillOnly setting, we either create filter for the exact one.
                // Otherwise, most likely it makes sense to create a separate filter for each of them.
                const exactInteractedDatapoint = datum.cell;
                const datapointsPerCharacteristic = datum.name;

                // remove keywords from `eventKeywords` which are duplicated by specific datapoints
                const keywordsOfDatapoints = Object.values(datapointsPerCharacteristic).flatMap(
                    datapoint => [datapoint.columnKey, datapoint.columnText],
                );
                const sharedKeywords = difference(eventKeywords, keywordsOfDatapoints);

                const filters$ =
                    currentApplyFilterOnExactDrillOnlySetting && exactInteractedDatapoint
                        ? [
                              this.querySelectDatapointToFilter(
                                  listenTo,
                                  filterDataArray,
                                  queryMetadata,
                                  exactInteractedDatapoint,
                                  [
                                      ...sharedKeywords,
                                      exactInteractedDatapoint.columnKey,
                                      exactInteractedDatapoint.columnText,
                                  ],
                                  { applyFilterToUpstreamHierarchyLevels },
                              ),
                          ]
                        : Object.values(datapointsPerCharacteristic).map(datapoint =>
                              this.querySelectDatapointToFilter(
                                  listenTo,
                                  filterDataArray,
                                  queryMetadata,
                                  datapoint,
                                  [...sharedKeywords, datapoint.columnKey, datapoint.columnText],
                                  { applyFilterToUpstreamHierarchyLevels },
                              ),
                          );

                const simpleSelectDatum: Datum = {
                    type: 'SIMPLE_SELECT',
                    name: datum.cell
                        ? datum.cell.key
                        : typeof datum.name === 'string' || Array.isArray(datum.name)
                        ? datum.name
                        : Object.keys(datum.name),
                };

                return forkJoin(filters$).pipe(
                    mergeMap(filters => {
                        return datum.type === 'QUERY_SIMPLE_SELECT' && filters.flat().length === 0
                            ? emitAsSimpleSelect(
                                  simpleSelectDatum,
                                  exactInteractedDatapoint || null,
                              )
                            : of(filters);
                    }),
                    map(filters => filters.flat()),
                    defaultIfEmpty<FilterDataArray, FilterDataArray>([]),
                );
            }

            default:
                return of([]);
        }
    }

    public static matchFirstListenToByKeywords(
        listenTo: ResolvedListenToDefinitions,
        keywords: Keyword[],
    ): ResolvedListenToDefinition | null {
        const interactiveListenTos = InteractionToFilterService.matchListenToByKeywords(
            listenTo,
            keywords,
        );

        return head(interactiveListenTos) || null;
    }

    public static matchListenToByKeywords(
        listenTo: ResolvedListenToDefinitions,
        keywords: Keyword[],
    ): ResolvedListenToDefinition[] {
        const interactiveListenTos = listenTo.filter(
            ({ interactionEnabled }) => interactionEnabled,
        );
        const noKeywordsDefined = interactiveListenTos.every(({ keywords }) => keywords === null);

        // preserve default behavior: if no keywords defined at all, consider only the first one
        if (noKeywordsDefined) return interactiveListenTos.slice(0, 1);

        const matchedListenTos = interactiveListenTos.filter(
            listenTo =>
                (listenTo.keywords && compareKeywords(listenTo.keywords, keywords)) ||
                compareKeywords(listenTo.defaultKeywords, keywords),
        );

        return matchedListenTos.length ? matchedListenTos : interactiveListenTos.slice(0, 1);
    }

    public static matchListenToByKeywordsForClearing(
        listenTo: ResolvedListenToDefinitions,
        keywords: Keyword[],
    ): ResolvedListenToDefinition[] {
        const interactiveListenTos = listenTo.filter(
            ({ interactionEnabled }) => interactionEnabled,
        );
        const noKeywordsDefined = interactiveListenTos.every(({ keywords }) => keywords === null);

        // preserve default behavior for clearing: if no keywords defined at all, consider all of them
        if (noKeywordsDefined) return interactiveListenTos;

        return interactiveListenTos.filter(
            listenTo =>
                (listenTo.keywords && compareKeywords(listenTo.keywords, keywords)) ||
                compareKeywords(listenTo.defaultKeywords, keywords),
        );
    }

    /**
     * Applies selection from multiple dimensions to current hierarchy filter.
     */
    private static mergeDimensionNodesIntoHierarchyFilter(
        dimensionNodes: DimensionNode[],
        exclude: boolean,
        hierarchy: Hierarchy,
        currentFilters: FilterDataArray,
        observedFilterItems: FilterId[],
        defaultFilterItemToEmit: FilterId | undefined,
        internalKeysAffectedBySelection: Array<Dimension['basedOnInternalKey']>,
        applyFilterToUpstreamHierarchyLevels: boolean,
    ): FilterData | null {
        const { item, nodes: currentFilterNodes } =
            InteractionToFilterService.findFilterItemAndNodesOfHierarchy(
                hierarchy,
                currentFilters,
                observedFilterItems,
                defaultFilterItemToEmit,
            );

        const dimensionNodesByInternalKey = groupBy(
            dimensionNodes,
            ({ basedOnInternalKey }) => basedOnInternalKey,
        );

        // walk the hierarchy structure depth-wise, and apply the nodes selected on each level
        const nextHierarchyFilterNodes = zip(hierarchy.internalKeys, hierarchy.basedOnInternalKeys)
            // since calling `mergeFilterNodes` with `[]` is not equal to not calling it at all
            // (it would be interpreted as deselection), we only want to call it when actually
            // the dimension has taken place in the interaction
            //
            // we also compare `basedOnInternalKey` instead of `internalKey`, so that we can try to apply
            // selection across modified dimensions that may contain duplicate nodes.
            // such dimensions are logically related, but do not share the `internalKey`
            .filter(([, basedOnInternalKey]) =>
                internalKeysAffectedBySelection.includes(basedOnInternalKey),
            )
            .reduce(
                (filterNodes, [internalKey, basedOnInternalKey]) =>
                    mergeFilterNodes(
                        filterNodes,
                        dimensionNodesByInternalKey[basedOnInternalKey] || [],
                        hierarchy,
                        internalKey,
                        { applyFilterToUpstreamHierarchyLevels },
                    ),
                currentFilterNodes,
            );

        return item
            ? {
                  item,
                  value: FilterValue.HIERARCHY({
                      value: {
                          hierarchyInternalKeys: hierarchy.internalKeys,
                          values: nextHierarchyFilterNodes,
                      },
                      exclude,
                  }),
              }
            : null;
    }

    private static translateNodesToHierarchy(
        nodes: DimensionNode[],
        hierarchy: Hierarchy,
        lookup: CrossHierarchyLookup | undefined,
    ): Array<Omit<HierarchyNode, 'children'>> {
        if (!lookup || !lookup[hierarchy.hierarchyId]) return [];

        const translatedNodes = nodes.flatMap(node => {
            if (!lookup[hierarchy.hierarchyId][node.basedOnInternalKey]) return [];
            if (!lookup[hierarchy.hierarchyId][node.basedOnInternalKey][node.key]) return [];

            return lookup[hierarchy.hierarchyId][node.basedOnInternalKey][node.key];
        });

        return uniqBy(translatedNodes, ({ identifier }) => identifier);
    }

    private static findFilterItemAndNodesOfHierarchy(
        hierarchy: Hierarchy,
        currentFilters: FilterDataArray,
        observedFilterItems: FilterId[],
        defaultFilterItemToEmit: FilterId | undefined,
    ): { item: FilterId | undefined; nodes: HierarchyFilterNode[] } {
        // caution: we make an assumption that `listenTo` may be `settings.HIERARCHY_ID` here,
        // if that is not the case we pick the default
        const item = observedFilterItems.includes(hierarchy.hierarchyId)
            ? hierarchy.hierarchyId
            : defaultFilterItemToEmit;

        const currentFilter = currentFilters.find(filter => filter.item === item);
        const currentFilterNodes =
            currentFilter && FilterValue.is.HIERARCHY(currentFilter.value)
                ? currentFilter.value.value.values
                : [];

        return { item, nodes: currentFilterNodes };
    }

    private querySelectDatapointToFilter(
        listenTo: ResolvedListenToDefinitions,
        filterDataArray: FilterDataArray,
        queryMetadata: PortletQuery | null,
        datapoint: QueryChartInteractionDatapoint | QueryChartInteractionDatapointMultiSelect,
        eventKeywords: Keyword[],
        settings: {
            applyFilterToUpstreamHierarchyLevels: boolean;
        },
    ): Observable<FilterDataArray> {
        // We first try to detect which `listenTo` should be activated based on the
        // portlet query configuration. We append that found listenTo to all other which
        // were *explicity* matched by keywords (not by the default logic of grabbing the first one).
        // If that fails, we do the standard matching by keywords.
        // Matching using query may provide us additional information about the event,
        // e.g. the internalKey of a dimension (additionalFilterContextFromQuery).
        const matchedFilterItemByQuery =
            queryMetadata && PortletQuery.interactionToListenTo(queryMetadata, listenTo, datapoint);
        const matchedFilterItemsByKeywords = InteractionToFilterService.matchListenToByKeywords(
            listenTo,
            eventKeywords,
        );
        const explicitlymatchedFilterItemsByKeyword = matchedFilterItemsByKeywords.filter(
            ({ keywords }) => keywords,
        );

        const additionalFilterContextFromQuery =
            matchedFilterItemByQuery && matchedFilterItemByQuery.matchedListenTo.interactionEnabled
                ? matchedFilterItemByQuery.context
                : MatchedListenToQueryContext.default();

        const matchingFilterItems =
            matchedFilterItemByQuery &&
            matchedFilterItemByQuery.matchedListenTo.interactionEnabled &&
            !explicitlymatchedFilterItemsByKeyword.includes(
                matchedFilterItemByQuery.matchedListenTo,
            )
                ? [matchedFilterItemByQuery.matchedListenTo].concat(
                      explicitlymatchedFilterItemsByKeyword,
                  )
                : matchedFilterItemsByKeywords;

        const filters$: Array<Observable<FilterData[]>> = matchingFilterItems.map(filterItem => {
            if (filterItem.filterHint)
                return this.filterTypeHintProcessor.processFilterTypeHint(
                    filterItem,
                    listenTo,
                    filterDataArray,
                    QueryChartInteractionDatapointUtil.toMultiSelect(datapoint).items.map(
                        ({ key }) => key,
                    ),
                    eventKeywords,
                    settings,
                    additionalFilterContextFromQuery,
                );

            if (!queryMetadata) return of([]);

            // fallback to default behavior
            const filterFromQuery = PortletQuery.interactionToFilter(
                queryMetadata,
                listenTo,
                datapoint,
            );

            return of(filterFromQuery ? [filterFromQuery] : []);
        });

        return forkJoin(filters$).pipe(
            map(filters => filters.flat()),
            defaultIfEmpty<FilterDataArray, FilterDataArray>([]),
        );
    }
}
